import React from 'react';
import { Grid, Typography, Divider } from '@mui/material';
import { useSelector } from 'react-redux';

import OutputTypeField from './OutputTypeField';
import OutputUriField from './OutputUriField';
import OutputEndpointUrlField from './OutputEndpointUrlField';
import OutputPathField from './OutputPathField';
import IntervalField from './IntervalField';
import OutputOptionsArchiveField from './OutputOptionsArchiveField';
import THIRD_PARTY_TYPES from '@/components/jobs/JobForm/thirdPartyTypes';
import JOB_TYPES from '@/components/jobs/types';

export default function OutputDataSection({
  values,
  errors,
  setValues,
  setErrors,
  setChanged,
  edit,
  type,
}) {
  const project_datastores = useSelector((state) => state.projectDatastores);

  const handleOutputTypeChange = (value) => {
    if (!value) {
      setValues({
        ...values,
        output_type: value,
        output_uri: '',
        output_options: undefined,
      });
      setErrors({
        ...errors,
        output_uri_error: false,
      });
    } else {
      const uriTest = Object.values(THIRD_PARTY_TYPES).find(
        (type) => type.value === value,
      )?.uriTest;
      const valid = uriTest ? uriTest.test(values.output_uri) : true;
      setValues({
        ...values,
        output_type: value,
        output_options: undefined,
      });
      setErrors({
        ...errors,
        output_uri_error: Boolean(values.output_uri) && !valid,
      });
    }
    setChanged();
  };

  const handleOutputUriChange = (value) => {
    if (
      [
        THIRD_PARTY_TYPES.REGIONAL.value,
        THIRD_PARTY_TYPES.TRAINML.value,
      ].includes(values.output_type)
    ) {
      setValues({
        ...values,
        output_uri: value,
      });
      setErrors({
        ...errors,
        output_uri_error: false,
      });
    } else {
      setValues({
        ...values,
        output_uri: value,
      });
    }
    setChanged();
  };

  const handleOutputEndpointUrlChange = (value) => {
    setValues({
      ...values,
      output_options: {
        ...values.output_options,
        endpoint_url: value,
      },
    });
    setChanged();
  };

  const handleOutputPathChange = (value) => {
    setValues({
      ...values,
      output_options: {
        ...values.output_options,
        path: value,
      },
    });
    setChanged();
  };

  const handleIntervalChange = (value) => {
    setValues({
      ...values,
      output_options: {
        ...values.output_options,
        interval: isNaN(parseInt(value)) ? '' : parseInt(value),
      },
    });
    setChanged();
  };

  const handleOutputArchiveChange = (value) => {
    setValues({
      ...values,
      output_options: {
        ...values.output_options,
        archive: value,
      },
    });
    setChanged();
  };

  return (
    <>
      <Grid size={12}>
        <Typography variant='h6'>Output Data</Typography>
        <Divider sx={{ mt: 1, mb: 2 }} />
      </Grid>
      <Grid size={12}>
        <OutputTypeField
          value={values.output_type}
          setValue={handleOutputTypeChange}
          setChanged={setChanged}
          edit={edit}
          type={type}
        />
      </Grid>
      {values.output_type === THIRD_PARTY_TYPES.REGIONAL.value ? (
        <>
          <Grid size={12}>
            <OutputUriField
              value={values.output_uri}
              outputType={values.output_type}
              error={errors.output_uri_error}
              setValue={handleOutputUriChange}
              setError={(error) => {
                setErrors({
                  ...errors,
                  output_uri_error: error,
                });
              }}
              setChanged={setChanged}
              edit={edit && type !== JOB_TYPES.NOTEBOOK.value}
              projectDatastores={project_datastores || []}
            />
          </Grid>
          <Grid size={12}>
            <OutputPathField
              value={values.output_options ? values.output_options.path : ''}
              error={errors.output_uri_error}
              setValue={handleOutputPathChange}
              setError={(error) => {
                setErrors({
                  ...errors,
                  output_uri_error: error,
                });
              }}
              setChanged={setChanged}
              edit={edit && type !== JOB_TYPES.NOTEBOOK.value}
              outputType={values.output_type}
              outputUri={values.output_uri}
              type={type}
            />
          </Grid>
        </>
      ) : values.output_type === THIRD_PARTY_TYPES.WASABI.value ? (
        <>
          <Grid size={12}>
            <OutputUriField
              value={values.output_uri}
              outputType={values.output_type}
              error={errors.output_uri_error}
              setValue={handleOutputUriChange}
              setError={(error) => {
                setErrors({
                  ...errors,
                  output_uri_error: error,
                });
              }}
              setChanged={setChanged}
              edit={edit}
            />
          </Grid>
          <Grid size={12}>
            <OutputEndpointUrlField
              value={
                values.output_options ? values.output_options.endpoint_url : ''
              }
              error={errors.output_endpoint_error}
              setValue={handleOutputEndpointUrlChange}
              setError={(error) => {
                setErrors({
                  ...errors,
                  output_endpoint_error: error,
                });
              }}
              setChanged={setChanged}
              edit={edit}
              outputType={values.output_type}
            />
          </Grid>
          <Grid size={12}>
            <OutputOptionsArchiveField
              value={
                values.output_options ? values.output_options.archive : false
              }
              setValue={handleOutputArchiveChange}
              setChanged={setChanged}
              edit={edit}
            />
          </Grid>
        </>
      ) : values.output_type &&
        values.output_type !== THIRD_PARTY_TYPES.TRAINML.value ? (
        <>
          <Grid size={12}>
            <OutputUriField
              value={values.output_uri}
              outputType={values.output_type}
              error={errors.output_uri_error}
              setValue={handleOutputUriChange}
              setError={(error) => {
                setErrors({
                  ...errors,
                  output_uri_error: error,
                });
              }}
              setChanged={setChanged}
              edit={edit}
            />
          </Grid>
          {values.output_type &&
          values.output_type !== THIRD_PARTY_TYPES.EVEFS.value &&
          values.output_type !== THIRD_PARTY_TYPES.REGIONAL.value ? (
            <Grid size={12}>
              <OutputOptionsArchiveField
                value={
                  values.output_options ? values.output_options.archive : false
                }
                setValue={handleOutputArchiveChange}
                setChanged={setChanged}
                edit={edit}
              />
            </Grid>
          ) : undefined}
        </>
      ) : values.output_type === THIRD_PARTY_TYPES.TRAINML.value ? (
        <>
          <Grid size={12}>
            <OutputUriField
              value={values.output_uri}
              outputType={values.output_type}
              error={errors.output_uri_error}
              setValue={handleOutputUriChange}
              setError={(error) => {
                setErrors({
                  ...errors,
                  output_uri_error: error,
                });
              }}
              setChanged={setChanged}
              edit={edit}
            />
          </Grid>
          {values.output_uri === 'checkpoint' ? (
            <Grid size={12}>
              <IntervalField
                value={
                  values.output_options ? values.output_options.interval : ''
                }
                error={errors.interval_error}
                setValue={handleIntervalChange}
                setError={(error) => {
                  setErrors({
                    ...errors,
                    interval_error: error,
                  });
                }}
                setChanged={setChanged}
                edit={edit}
                outputType={values.output_type}
              />
            </Grid>
          ) : undefined}
          <Grid size={12}>
            <OutputOptionsArchiveField
              value={
                values.output_options ? values.output_options.archive : false
              }
              setValue={handleOutputArchiveChange}
              setChanged={setChanged}
              edit={edit}
            />
          </Grid>
        </>
      ) : undefined}
    </>
  );
}
